/**
 * CaptionProfilesEditor — Tank Redesign Caption Builder.
 * Source: design_handoff_tank/screens-rest.jsx § Screen08CaptionBuilder.
 *
 * 3-pane layout (320 / flex / 320):
 *   • Left   — Variable library, grouped (Match / Persons / Photo).
 *              Filter input. Click to insert at the focused field's caret.
 *   • Center — Template fields (Title / Headline / Caption / Keywords /
 *              Credit / Copyright). Variables render as phosphor chips
 *              inline with the prose text.
 *   • Right  — Resolved live preview against a sample context.
 *
 * Persistence: GET/PUT /api/company/iptc-defaults/. PUT requires
 * `can_manage_company`. The same templates flow into the IPTC writer
 * at export time, so this surface IS the company's caption source.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchCompanyIptcDefaults,
  updateCompanyIptcDefaults,
  fetchIptcPreview,
} from '../../../../api';
import styles from './CaptionProfilesEditor.module.css';

// ---------------------------------------------------------------------------
// Variable library — grouped chips per the handoff prototype.
// ---------------------------------------------------------------------------

const VARIABLE_GROUPS = [
  {
    key: 'match',
    name: 'Match',
    vars: [
      '{home_team}',
      '{away_team}',
      '{competition}',
      '{round}',
      '{season}',
      '{date}',
      '{kickoff}',
      '{venue}',
      '{city}',
      '{country}',
      '{country_code}',
    ],
  },
  {
    key: 'persons',
    name: 'Persons',
    vars: ['{persons}', '{photographer}'],
  },
  {
    key: 'photo',
    name: 'Photo',
    vars: ['{filename}', '{capture_date}', '{match_phase}'],
  },
];

// ---------------------------------------------------------------------------
// Template fields — Title / Headline / Caption / Keywords / Credit / Copyright.
// `multi: true` switches to a tall textarea; `field` is the API body key.
// ---------------------------------------------------------------------------

const TEMPLATE_FIELDS = [
  { key: 'title_template', label: 'Title', multi: false },
  { key: 'headline_template', label: 'Headline', multi: false },
  { key: 'caption_template', label: 'Caption', multi: true },
  { key: 'keywords_template', label: 'Keywords', multi: true, omit: true },
  { key: 'credit', label: 'Credit', multi: false },
  { key: 'copyright_notice', label: 'Copyright', multi: false },
];

// ---------------------------------------------------------------------------
// Variable token shape — `{var_name}` (lowercase + underscore + optional
// colon for namespacing). Used for both detection and serialization.
// ---------------------------------------------------------------------------

const VARIABLE_RE = /\{[a-z_:]+\}/gi;

// ---------------------------------------------------------------------------
// Sample context for the live preview when no real photo is available.
// Mirrors the values shown in the prototype to make visual review easy.
// ---------------------------------------------------------------------------

const SAMPLE_CONTEXT = {
  filename: 'A1B0845',
  home_team: 'Excelsior Rotterdam',
  away_team: 'FC Utrecht',
  competition: 'Eredivisie',
  round: '31',
  season: '2025/2026',
  date: '28 April 2026',
  kickoff: '14:30',
  venue: 'Stadion Woudestein',
  city: 'Rotterdam',
  country: 'Netherlands',
  country_code: 'NLD',
  persons: 'Quinten Timber, Santiago Giménez',
  photographer: 'Yannick Verhoeven',
  capture_date: '2026-04-28',
  match_phase: '1st half',
};

// Naive client-side template substitution — used as a fallback while the
// /api/iptc/preview/ endpoint is in flight or unreachable. The real
// preview comes from the backend so server-side IPTC rendering stays
// authoritative.
function naiveSubstitute(template, ctx) {
  if (!template) return '';
  return template.replace(VARIABLE_RE, (match) => {
    const key = match.slice(1, -1);
    return ctx[key] != null ? String(ctx[key]) : match;
  });
}

// ---------------------------------------------------------------------------

function VariableGroup({ group, filter, onInsert }) {
  const filtered = group.vars.filter((v) => v.toLowerCase().includes(filter.toLowerCase()));
  if (!filtered.length) return null;
  return (
    <div className={styles.varGroup}>
      <div className={styles.varGroupName}>{group.name}</div>
      <div className={styles.varChipRow}>
        {filtered.map((v) => (
          <button
            key={v}
            type="button"
            className={styles.varChipBtn}
            onClick={() => onInsert(v)}
            title={`Insert ${v}`}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// contentEditable utilities — serialise DOM ↔ template string.
//
// Variables live as <span data-variable="{name}" contenteditable="false">
// inside the editable div, so they're "atomic" from the caret's POV (you
// can't put the cursor inside the chip text, only before/after it). Real
// text is plain text nodes; <br> / <div> are normalised to "\n".
// ---------------------------------------------------------------------------

function serializeEditableNode(root) {
  let out = '';
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent || '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node;
    const variable = el.getAttribute && el.getAttribute('data-variable');
    if (variable) {
      out += variable;
      return;
    }
    if (el.tagName === 'BR') {
      out += '\n';
      return;
    }
    // <div> in contentEditable typically marks a new line (Chrome inserts
    // them on Enter). Inject a leading \n if we're not at the start.
    if (el.tagName === 'DIV' && out.length > 0 && !out.endsWith('\n')) {
      out += '\n';
    }
    for (const child of el.childNodes) walk(child);
  };
  for (const child of root.childNodes) walk(child);
  return out;
}

function buildChipElement(variable) {
  const el = document.createElement('span');
  el.className = styles.varChip;
  el.setAttribute('data-variable', variable);
  el.setAttribute('contenteditable', 'false');
  el.textContent = variable;
  return el;
}

function seedEditableFromValue(root, value) {
  // Wipe + rebuild. Only called when the contentEditable is *not* the
  // source of truth (initial mount, external value change while
  // unfocused). Don't call while the user has the caret in the field —
  // it nukes their selection.
  root.innerHTML = '';
  if (!value) return;
  let lastIndex = 0;
  for (const match of value.matchAll(VARIABLE_RE)) {
    if (match.index > lastIndex) {
      root.appendChild(document.createTextNode(value.slice(lastIndex, match.index)));
    }
    root.appendChild(buildChipElement(match[0]));
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < value.length) {
    const tail = value.slice(lastIndex);
    // Convert literal newlines to <br> so the editor renders them.
    const lines = tail.split('\n');
    lines.forEach((line, i) => {
      if (i > 0) root.appendChild(document.createElement('br'));
      if (line) root.appendChild(document.createTextNode(line));
    });
  }
}

function placeCaretAtEnd(root) {
  const range = document.createRange();
  range.selectNodeContents(root);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

// ---------------------------------------------------------------------------

function TemplateField({ field, value, focused, onFocus, onChange, registerInputRef }) {
  const editorRef = useRef(null);
  // Track whether the editor owns the current value (user is editing).
  // While owned, we don't re-seed from the `value` prop — that would
  // wipe the caret and cause cursor jumps mid-typing.
  const ownsValueRef = useRef(false);
  const lastSerializedRef = useRef(value);

  // Seed the editable on mount, and re-seed whenever the upstream value
  // changes from a source that isn't the editor itself (variable
  // insertion from the sidebar, save round-trip, etc.).
  useEffect(() => {
    const root = editorRef.current;
    if (!root) return;
    if (ownsValueRef.current && value === lastSerializedRef.current) {
      // Value matches what we just serialized — no re-seed needed.
      return;
    }
    seedEditableFromValue(root, value || '');
    lastSerializedRef.current = value;
    ownsValueRef.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    const root = editorRef.current;
    if (!root) return;
    const next = serializeEditableNode(root);
    ownsValueRef.current = true;
    lastSerializedRef.current = next;
    onChange(field.key, next);
  }, [field.key, onChange]);

  const handleFocus = useCallback(() => {
    onFocus(field.key);
  }, [field.key, onFocus]);

  // Expose a minimal "input-like" API to the parent so the existing
  // insert-at-caret logic + ref registry still works without forcing
  // a sweeping refactor.
  const registerNode = useCallback(
    (el) => {
      editorRef.current = el;
      if (!el) {
        registerInputRef(field.key, null);
        return;
      }
      registerInputRef(field.key, {
        focus: () => el.focus(),
        // No selectionStart/End on contentEditable — the parent's
        // insertVariable handler now uses insertChipAtCaret() below.
        __isContentEditable: true,
        node: el,
        insertChipAtCaret: (variable) => {
          const sel = window.getSelection();
          let range;
          if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
            range = sel.getRangeAt(0);
          } else {
            // No active selection inside the field — append at end.
            range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
          }
          range.deleteContents();
          const chip = buildChipElement(variable);
          range.insertNode(chip);
          // Move caret after the chip so subsequent typing flows.
          range.setStartAfter(chip);
          range.setEndAfter(chip);
          sel?.removeAllRanges();
          sel?.addRange(range);
          // Re-emit serialised value so the parent state stays in sync.
          handleInput();
        },
        focusEnd: () => {
          el.focus();
          placeCaretAtEnd(el);
        },
      });
    },
    [field.key, handleInput, registerInputRef],
  );

  return (
    <div className={styles.templateField}>
      <label className={styles.fieldLabel}>{field.label}</label>
      <div className={styles.fieldShell}>
        {/* contentEditable replaces the old transparent-input + highlight
            overlay. Variable chips are real `<span data-variable>`
            inline-blocks with `contenteditable=false`, so they read at
            the same width as the underlying token text and the caret
            never drifts. */}
        <div
          ref={registerNode}
          className={`${styles.fieldInput} ${field.multi ? styles.fieldInputMulti : ''}`}
          contentEditable="true"
          suppressContentEditableWarning
          onInput={handleInput}
          onFocus={handleFocus}
          spellCheck={false}
          role="textbox"
          aria-label={field.label}
          data-placeholder="Enter template…"
        />
        {focused && <div className={styles.fieldFocusRing} aria-hidden="true" />}
      </div>
    </div>
  );
}

export default function CaptionProfilesEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [filter, setFilter] = useState('');
  const [focusedField, setFocusedField] = useState('caption_template');
  const [values, setValues] = useState({});
  const [previewLines, setPreviewLines] = useState({});
  const [dirty, setDirty] = useState(false);
  const inputRefs = useRef({});

  const registerInputRef = useCallback((key, el) => {
    inputRefs.current[key] = el;
  }, []);

  // Load existing defaults on mount.
  useEffect(() => {
    let cancelled = false;
    fetchCompanyIptcDefaults()
      .then((data) => {
        if (cancelled) return;
        const seed = {};
        for (const f of TEMPLATE_FIELDS) {
          if (f.omit) continue;
          seed[f.key] = data?.[f.key] ?? '';
        }
        setValues(seed);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Failed to load defaults');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Refresh the live preview whenever the templates change. Debounced
  // so we don't hammer /api/iptc/preview/ on each keystroke.
  useEffect(() => {
    if (loading) return;
    const handle = setTimeout(async () => {
      const templates = {
        title_template: values.title_template || '',
        headline_template: values.headline_template || '',
        caption_template: values.caption_template || '',
      };
      try {
        const data = await fetchIptcPreview(templates, SAMPLE_CONTEXT);
        setPreviewLines({
          title: data?.title || naiveSubstitute(templates.title_template, SAMPLE_CONTEXT),
          headline: data?.headline || naiveSubstitute(templates.headline_template, SAMPLE_CONTEXT),
          caption: data?.caption || naiveSubstitute(templates.caption_template, SAMPLE_CONTEXT),
          credit: values.credit || '',
          copyright: values.copyright_notice || '',
        });
      } catch {
        // Backend preview unreachable — fall back to client substitution.
        setPreviewLines({
          title: naiveSubstitute(templates.title_template, SAMPLE_CONTEXT),
          headline: naiveSubstitute(templates.headline_template, SAMPLE_CONTEXT),
          caption: naiveSubstitute(templates.caption_template, SAMPLE_CONTEXT),
          credit: values.credit || '',
          copyright: values.copyright_notice || '',
        });
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [values, loading]);

  const handleChange = useCallback((key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  const handleInsertVariable = useCallback(
    (variable) => {
      const target = focusedField || 'caption_template';
      const handle = inputRefs.current[target];
      // contentEditable path — TemplateField exposes a chip-at-caret
      // method that mutates the live DOM and re-emits the serialized
      // value. The setValues call falls out of the onChange callback
      // inside that path, so we just need to focus + insert here.
      if (handle?.__isContentEditable && handle.insertChipAtCaret) {
        handle.focus();
        handle.insertChipAtCaret(variable);
        setDirty(true);
        return;
      }
      // Legacy <input>/<textarea> path (kept for any TemplateField
      // variant that opts out of contentEditable in the future).
      const current = values[target] || '';
      let next = current;
      if (handle && typeof handle.selectionStart === 'number') {
        const start = handle.selectionStart ?? current.length;
        const end = handle.selectionEnd ?? current.length;
        next = current.slice(0, start) + variable + current.slice(end);
      } else {
        next = current + variable;
      }
      setValues((prev) => ({ ...prev, [target]: next }));
      setDirty(true);
      requestAnimationFrame(() => {
        if (handle) {
          handle.focus?.();
          const caret = (handle.selectionStart ?? current.length) + variable.length;
          if (typeof handle.setSelectionRange === 'function') {
            handle.setSelectionRange(caret, caret);
          }
        }
      });
    },
    [focusedField, values],
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {};
      for (const f of TEMPLATE_FIELDS) {
        if (f.omit) continue;
        payload[f.key] = values[f.key] ?? '';
      }
      await updateCompanyIptcDefaults(payload);
      setSavedAt(new Date());
      setDirty(false);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const lastSavedLabel = useMemo(() => {
    if (!savedAt) return null;
    return `saved ${savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }, [savedAt]);

  if (loading) {
    return <div className={styles.loading}>Loading caption profile…</div>;
  }

  return (
    <div className={styles.editor}>
      {/* Left — variables */}
      <aside className={styles.varPane}>
        <div className={styles.paneEyebrow}>VARS · Variables</div>
        <input
          className={styles.varFilter}
          type="text"
          placeholder="Filter variables"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className={styles.varList}>
          {VARIABLE_GROUPS.map((g) => (
            <VariableGroup key={g.key} group={g} filter={filter} onInsert={handleInsertVariable} />
          ))}
        </div>
      </aside>

      {/* Center — template canvas */}
      <section className={styles.templatePane}>
        <header className={styles.templateHeader}>
          <div className={styles.profileEyebrow}>PROFILE · Company default</div>
          <div className={styles.headerSep}>·</div>
          <div className={styles.profileMeta}>
            {dirty ? 'unsaved changes' : (lastSavedLabel || 'no changes')}
          </div>
          <div className={styles.headerSpacer} />
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </header>
        {error && <div className={styles.errorBanner}>{error}</div>}
        <div className={styles.templateBody}>
          {TEMPLATE_FIELDS.filter((f) => !f.omit).map((f) => (
            <TemplateField
              key={f.key}
              field={f}
              value={values[f.key] || ''}
              focused={focusedField === f.key}
              onFocus={setFocusedField}
              onChange={handleChange}
              registerInputRef={registerInputRef}
            />
          ))}
        </div>
      </section>

      {/* Right — live preview */}
      <aside className={styles.previewPane}>
        <div className={styles.paneEyebrow}>PREVIEW · {SAMPLE_CONTEXT.filename}.JPG</div>
        <div className={styles.previewBox}>
          {[
            { k: 'Title', v: previewLines.title },
            { k: 'Headline', v: previewLines.headline },
            { k: 'Caption', v: previewLines.caption },
            { k: 'Credit', v: previewLines.credit },
            { k: 'Copyright', v: previewLines.copyright },
          ].map(({ k, v }) => (
            <div key={k} className={styles.previewLine}>
              <div className={styles.previewKey}>{`// ${k}`}</div>
              <div className={styles.previewValue}>{v || <span className={styles.placeholder}>—</span>}</div>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
